import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Text, Flex, Link } from 'rebass/styled-components';
import { Logo, ProfilePicture, Button } from '@getro/rombo';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { UserNetworkLogo } from 'components/organisms/contactActionPanel/userNetworkLogo';
import ContactsSharedListSchema from '../../../schemas/contactsSharedList';
import SubscribeToSlackButton from '../subscribeToSlackButton';
import SubscribeViaEmailButton from '../subscribeViaEmailButton';
import { trackEvent } from '../../../helpers/analytics';
import HowItWorks from '../howItWorks';

const FixedHeader = ({ listInfo }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showToggle, setShowToggle] = useState(false);
  const descriptionRef = useRef(null);

  const trackPoweredByLinkClick = () => {
    trackEvent('contacts:shared_list:powered_by_click', {
      list_slug: listInfo.slug,
      collection_id: listInfo.collection.id,
      origin: 'header',
    });
  };

  // Check if description needs truncation
  const checkDescriptionHeight = useCallback(() => {
    if (descriptionRef.current && listInfo.description) {
      const element = descriptionRef.current;
      const computedLineHeight = window.getComputedStyle(element).lineHeight;
      const lineHeight = computedLineHeight === 'normal' ? 20 : parseFloat(computedLineHeight);
      const maxHeight = 59; // 3 lines

      // Reset to check natural height
      element.style.maxHeight = 'none';
      const naturalHeight = element.scrollHeight;
      const isLongDescription = naturalHeight > maxHeight;

      // Set max height based on description length and expanded state
      if (!isLongDescription) {
        element.style.maxHeight = 'none';
      } else {
        element.style.maxHeight = isExpanded ? 'none' : `${lineHeight * 2.5}px`;
      }

      setShowToggle(isLongDescription);
    }
  }, [listInfo.description, isExpanded]);

  useEffect(() => {
    checkDescriptionHeight();

    // Add resize listener
    window.addEventListener('resize', checkDescriptionHeight);

    // Cleanup
    return () => {
      window.removeEventListener('resize', checkDescriptionHeight);
    };
  }, [checkDescriptionHeight]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Flex flexDirection="column" sx={{ gap: '16px', pt: ['32px', '24px'], minHeight: '150px' }}>
      <Flex sx={{ gap: '16px', flexDirection: 'column' }}>
        <Flex flexDirection={['column-reverse', 'row', 'row']} alignItems={['flex-start', 'center', 'center']}>
          <Flex flexGrow="1" mt={['32px', 0, 0]} flexDirection="row" sx={{ gap: '16px', alignItems: 'center' }}>
            <Text fontSize="24px" fontWeight="700" color="text.dark" lineHeight="120%">
              {listInfo.name}
            </Text>
            <Flex
              flexDirection="row"
              sx={{ gap: '8px', width: ['100%', 'auto', 'auto'] }}
              alignItems="center"
              display={['none !important', 'none !important', 'flex !important']}
            >
              <SubscribeToSlackButton sx={{ width: ['100%', 'auto', 'auto'] }} listInfo={listInfo} />
              <SubscribeViaEmailButton
                mr={[0, '8px', '8px']}
                sx={{ width: ['100%', 'auto', 'auto'] }}
                listInfo={listInfo}
              />
            </Flex>
          </Flex>
          <Box
            sx={{
              columnGap: '16px',
              width: ['100%', 'unset', 'unset'],
              flexDirection: ['row-reverse', 'row', 'row'],
              display: 'flex',
            }}
            alignItems="baseline"
            alignContent="baseline"
          >
            <Link
              href="https://www.getro.com"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ flexGrow: [1, 'unset', 'unset'] }}
              onClick={trackPoweredByLinkClick}
            >
              <Logo color="primary" width="96px" />
            </Link>
          </Box>
        </Flex>
        {listInfo.description && (
          <Box>
            <Box
              ref={descriptionRef}
              sx={{
                fontSize: '14px',
                lineHeight: '1.4',
                overflow: 'hidden',
                transition: 'max-height 0.3s ease-in-out',
              }}
            >
              {listInfo.description.split('\n').map((item) => (
                <Text key={item}>{item}</Text>
              ))}
            </Box>
            {showToggle && (
              <Button
                size="small"
                variant="tertiary"
                icon={isExpanded ? ChevronUp : ChevronDown}
                iconPosition="right"
                onClick={toggleExpanded}
                sx={{
                  mt: '8px',
                }}
              >
                {isExpanded ? 'See less' : 'See more'}
              </Button>
            )}
          </Box>
        )}
      </Flex>
      <Box
        sx={{
          gap: '8px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {!listInfo.introRequestsEnabled && (
          <Flex flexDirection="row" alignItems="center" flexGrow={[1, 0, 0]}>
            <Box flex="0 0 auto" width={['40px', '40px', '48px']} height={['40px', '40px', '48px']} mr="8px">
              <ProfilePicture
                variant="square"
                name={listInfo.collection?.name}
                imageUrl={listInfo.collection?.logoUrl}
              />
            </Box>
            <Text fontSize="14px">
              {'A list by '}
              <Text as="span" fontWeight="medium">
                {listInfo.collection?.name}
              </Text>
            </Text>
          </Flex>
        )}
        <Flex sx={{ gap: '8px', flexDirection: ['column-reverse', 'column'] }}>
          {listInfo.introRequestsEnabled && (
            <Flex sx={{ gap: '16px', alignItems: 'center', mb: ['16px', '8px', '0'] }}>
              <UserNetworkLogo
                user={listInfo.introRequestManager}
                network={listInfo.collection}
                width="40px"
                height="40px"
              />

              <Flex
                sx={{
                  flexDirection: ['column', 'row'],
                  alignItems: ['flex-start', 'center'],
                  justifyContent: 'flex-start',
                  gap: ['0', '4px'],
                }}
              >
                <Text fontSize="14px" color="text.main" sx={{ display: 'inline', alignItems: 'center', gap: '4px' }}>
                  {listInfo?.introRequestManager?.fullName} is offering intros to these contacts.{' '}
                </Text>
                <HowItWorks listInfo={listInfo} />
              </Flex>
            </Flex>
          )}
          <Box
            flexDirection={['column', 'row', 'row']}
            sx={{ gap: '8px', width: ['100%', 'auto', 'auto'] }}
            alignItems="center"
            display={['flex', 'flex', 'none']}
          >
            <SubscribeToSlackButton sx={{ width: ['100%', 'auto', 'auto'] }} listInfo={listInfo} />
            <SubscribeViaEmailButton
              mr={[0, '8px', '8px']}
              sx={{ width: ['100%', 'auto', 'auto'] }}
              listInfo={listInfo}
            />
          </Box>
        </Flex>
      </Box>
    </Flex>
  );
};

FixedHeader.propTypes = {
  listInfo: ContactsSharedListSchema,
};

FixedHeader.defaultProps = {
  listInfo: null,
};

export default FixedHeader;
